import { asyncScheduler, timer, Observable } from 'rxjs';
import { catchError, takeWhile, switchMapTo } from 'rxjs/operators';
/**
 * Provides a simple way to create an Observable with a n values that will
 * always end with an error. It is the error version of "of()".
 *
 * ## Examples
 *
 * Similar to `of(1,2,3)`:
 *
 * ```ts
 * doomed(1,2,3)().subscribe(console.log, console.log);
 * // 1, 2, 3, Observable values exhausted.
 * ```
 *
 * Or with an explicit error:
 *
 * ```ts
 * doomed(1,2)('three is a crowd').subscribe(console.log, console.log);
 * // 1, 2, 3, three is a crowd
 * ```
 *
 * This can be used with `catchError` to produce a final "good" result and then
 * rethrow the underlying error.
 *
 * ```ts
 * of(1, 2, 3).pipe(
 *   map(n => {
 *     if (n === 3) {
 *       throw 'three is a crowd';
 *     }
 *     return n;
 *   }),
 *   catchError((err, caught) => doomed(0)(err))
 * )
 * .subscribe(console.log, console.log);
 * // 1, 2, 0, three is a crowd
 * ```
 */
export const doomed = (...values) => error => new Observable(function (subscriber) {
  values.forEach(value => subscriber.next(value));
  subscriber.error(error ?? 'Observable values exhausted.');
});

/**
 * Provides a catchError alternative that is also called with the offending
 * Observable's last emitted value.
 */

export function cacheCatchError(handler) {
  return function (source) {
    let cached;
    return new Observable(subscriber => {
      const next = val => {
        cached = val;
        subscriber.next(val);
      };
      const error = error => subscriber.error(error);
      const complete = () => subscriber.complete();
      const subscription = source.subscribe({
        next,
        error,
        complete
      });
      return () => subscription.unsubscribe();
    }).pipe(catchError((e, caught) => handler(e, cached, caught)));
  };
}

/**
 * Polls by re-trying source Observable until predicate is no-longer satisfied.
 *
 * @param intervalMs - The delay in milliseconds to wait between polling
 * @param predicate - The predicate source observable's value is passed to and
 *   whose result is evaluated for whether polling is complete.
 * @param scheduler - The scheduler to use to schedule the delay. Defaults to
 *   rjxs/asyncScheduler.
 *
 * TODO(steckel): This needs a maxAttempts parameter to prevent really evil
 * recursion.
 */
export const poll = (intervalMs, predicate, scheduler = asyncScheduler) => source => timer(0, intervalMs, scheduler).pipe(switchMapTo(source), takeWhile(value => !predicate(value), true));

/**
 * Creates an output Observable which concurrently emits all values from every
 * given input Observable where the output Observable completion mirrors the
 * first source observable to complete.
 *
 * "Similar to merge, but when you want to complete upon the first source
 * observable's completion."
 *
 * source a: -a---a---a---a---a-
 * source b: ---b---b|
 * result  : -a-b-a-b|
 */

export function exhaustRace(sources) {
  return new Observable(subscriber => {
    let racing = true;
    const subscriptions = [];
    for (let i = 0; racing && !subscriber.closed && i < sources.length; i++) {
      const next = value => subscriber.next(value);
      const error = e => {
        if (racing) {
          // We're still racing, but we ruined it for everyone.
          racing = false;
          subscriber.error(e);
          // eslint-disable-next-line unicorn/no-for-loop
          for (let s = 0; s < subscriptions.length; s++) {
            if (s !== i) {
              subscriptions[s].unsubscribe();
            }
          }
        }
      };
      const complete = () => {
        if (racing) {
          // We're still racing, but we won! So unsubscribe
          // all other subscriptions that we have, except this one.
          racing = false;
          subscriber.complete();
          // eslint-disable-next-line unicorn/no-for-loop
          for (let s = 0; s < subscriptions.length; s++) {
            if (s !== i) {
              subscriptions[s].unsubscribe();
            }
          }
        }
      };
      subscriptions.push(sources[i].subscribe({
        next,
        error,
        complete
      }));
    }
  });
}